/*
defines node interactions
*/

import NodeView from 'view/node';

/**
 * defines a class containing handlers for all nodes.
 * there is one instance of this class for all nodes.
 */
export default class NodeController {
    /**
     *  makes a new controller for all nodes
     * @param {GraphController} graphController the main graph controller to use
     * @param {Graph} model the graph model to use
     * @param {GraphView} view the graph view to use
     */
    constructor(graphController, model, view) {
        this.g = model;
        this.view = view;
        this.graphController = graphController;

        // number of times the help info was shown
        this._helped = 0;
        this.clickThreshold = 200;
    }

    /**
     * initializes and attaches the node handlers
     */
    initHandlers() {
        this.defineHandlers();
        this.bindHandlers();
    }

    /**
     * attaches the node handlers
     */
    bindHandlers() {
        this.hoverNodeHandler();
        this.clickNodeHandler();
        this.dragHandler();
        NodeView.plus_button_selection.on('click', (nodeView) => {
            nodeView.toggleTooltip();
        });
    }

    /**
     * binds the handler for dragging nodes
     *
     * CHANGED: double click required to search for extra nodes
     */
    dragHandler() {
        console.log('in drag handler');
        NodeView.node_selection
            .on('dblclick', (nodeView) => this.onClickSearchFromNode(nodeView))
            .on('click.all', (nodeView) => this.onClick(nodeView))
            .call(d3.drag()
            .on('start.all', (nodeView) => this.onDragStart(nodeView))
            .on('drag.all', (nodeView) => this.onDrag(nodeView))
            .on('end.all', (nodeView) => this.onDragEnd(nodeView))
        );
    }

    /**
     * binds the handler for clicking any node
     */
    clickNodeHandler() {
        console.log('mouse position', d3.event)
        d3.selectAll('.wikiLink').on('click.link', () => {
            d3.event.preventDefault();
            d3.event.stopPropagation();
            d3.event.stopImmediatePropagation();
            this.graphController
                    .addConnectorNode(d3.event.target.innerText || 'test');
        });
    }

    /**
     * sets up the handlers for hovering over a node
     */
    hoverNodeHandler() {
        NodeView.node_selection
            .on('mouseover', (nodeView) => this.onNodeMouseOver(nodeView))
            .on('mouseout', (nodeView) => this.onNodeMouseOut(nodeView))
            .on('mousedown', (nodeView) => this.onNodeMouseDown(nodeView))
            .on('mouseup', (nodeView) => this.onNodeMouseUp(nodeView));
    }

    /**
     * defines the node handlers
     */
    defineHandlers() {
        this.onNodeMouseOver = this.onNodeMouseOverHelp();
    }

    /**
     * shows the tooltip on hover
     * @return {Function}
     */
    onNodeMouseOverHelp() {
        return (nodeView) => {
            // console.log('mouseover', nodeView.id);
            if (nodeView.id) {
                nodeView.hover();
            }

            if (this._helped < 3) {
                this.view.div.classed('help', true);
                this._helped++;
            } else {
                this.view.div.classed('help', false);
            }
            d3.selectAll('.string .word')
                .on('mouseover.word', () => this.onMetaDataMouseOver())
                .on('mouseout.word', () => this.onMetaDataMouseOut())
                .on('click.word', () => this.onMetaDataClick());
        };
    }

    /**
     * handle mouse out within node
     * @param {NodeView} nodeView
     */
    onNodeMouseOut(nodeView) {
        nodeView.unhover();
    }


    /**
     * handle mouse up within node
     * @param {NodeView} nodeView
     */
    onNodeMouseUp(nodeView) {
        let clicked = Date.now() - this.mouseDownedTime < this.clickThreshold;
        if (clicked && !this.mouseMoved && nodeView.type !== 'connector') {
            this.onClick(nodeView);
        }
        this.mouseMoved = false;
    }


    /**
     * handle mouse move within node
     */
    onNodeMouseMove() {
        this.mouseMoved = true;
    }

    /**
     * handle mouse down within node
     */
    onNodeMouseDown() {
        this.mouseDownedTime = Date.now();
        console.log(this.mouseDownedTime);
    }

    /**
     * starts moving this node when the mouse starts to drag this node
     * @param {NodeView} nodeView
     */
    onDragStart(nodeView) {
        console.log('state at ondragstart',this.graphController.state.is('none'));
        if (this.graphController.state.is('cropping')) {
            if (nodeView.type !== 'image') return;
            this.onClipDragStart(nodeView);
        } else if (this.graphController.state.is('none')) {
            this.graphController.initPanHandler();
            if (!d3.event.active) {
                this.view.simulation.alphaTarget(0.3).restart();
            }
            nodeView.fix();
            nodeView.unfade();
            nodeView.grow();
            nodeView._dragging = true;
            d3.select('.gnode-' + nodeView.id).raise();
            this.view.showAllLinksToNode(nodeView.id);
            this.mouseDownedTime = Date.now();
        }
    }

    /**
     * moves this node when the mouse drags this node
     * @param {NodeView} nodeView
     */
    onDrag(nodeView) {
        switch (this.graphController.state.get()) {
            case 'NONE':
                nodeView.fx = d3.event.x;
                nodeView.fy = d3.event.y;
                this.mouseMoved = true;
                break;
            case 'CROPPING':
                if (nodeView.type === 'image') {
                    this.onClipDragMove(nodeView);
                }
                break;
            default:
                break;
        }
    }

    /**
     * stops moving this node when the mouse button is released
     * @param {NodeView} nodeView
     */
    onDragEnd(nodeView) {
        if (this.graphController.state.is('NONE')) {
            if (!d3.event.active) this.view.simulation.alphaTarget(0);
            nodeView.unfix();
            nodeView._dragging = false;

            // let timeToClick = Date.now() - this.mouseDownedTime;
            // if (timeToClick < this.clickThreshold && !this.mouseMoved) {
            //     if (nodeView.type !== 'connector') {
            //         this.onClick(nodeView)
            //     }
            // }

            this.mouseMoved = false;
        } else if (this.graphController.state.is('CROPPING')) {
            if (nodeView.type === 'image') {
                this.onClipDragEnd(nodeView);
                this.graphController.state.set('NONE');
            }
        }
    }

    /**
     * handle node clicking
     * @param {NodeView} nodeView
     */
    onClick(nodeView) {
        // console.log('node clicked');
        switch (this.graphController.state.get()) {
            case 'LINKING':
                this.onClickLinkNode(nodeView);
                break;
            case 'INTERPOLATING':
                this.onClickInterpolateNode(nodeView);
                break;
            case 'DELETING':
                this.onClickDeleteNode(nodeView);
                break;
            case 'FOCUSING':
                this.onClickFocusNode(nodeView);
                break;
            case 'CASTING':
                this.onClickCastNode(nodeView);
                break;
            // doesn't listen to single click searches anymore, can delete
            // default:
            //     this.onClickSearchFromNode(nodeView);
            //     break;
        }
    }

    /**
     * start new nodes from the given node
     * @param {NodeView} nodeView
     */
    onClickSearchFromNode(nodeView) {
        let searchQuery;

        if (nodeView.node.isEmpty()) {
            searchQuery = '';
        } else {
            const word = nodeView.node.title.split(" ").filter(el=> el.toLowerCase()!=="the");
            searchQuery = word.length > 2 ? word[0].toLowerCase() : word.join(" ");
        }

        this.graphController.mosaic(searchQuery, nodeView);
        this.view.div.classed('mosaic', true);
    }

    /**
     * focus on the given node
     * @param {NodeView} nodeView
     */
    onClickFocusNode(nodeView) {
        this.view.lightbox.classed('on', true);
        this.view.lightboxImage.attr('src', nodeView.node.image.url);
    }

    /**
     * delete the given node
     * @param {NodeView} nodeView
     */
    onClickDeleteNode(nodeView) {
        this.graphController.removeNode(nodeView.id);
        // this.graphController.state.set('NONE');
    }

    /**
     * cast the given node
     * @param {NodeView} nodeView
     */
    onClickCastNode(nodeView) {
      console.log('click cast node', nodeView)
      this.graphController.state.set('NONE');
      if (nodeView.node.getImage) {
        let image=nodeView.node.getImage();
        if (image.url) {
          // $.post("https://pathways-imgserver.herokuapp.com/control", {url: url});
          this.graphController.daydreams.castImageToDevice(
            this.graphController.currentCastDevice,
            image.url
          );
          // $.post("http://localhost:5000/control", {url: image.url});
        }
      }
    }

    /**
     * interpolate the given node
     * @param {NodeView} nodeView
     */
    onClickInterpolateNode(nodeView) {
        if (!this.hasOwnProperty('firstNode')) {
            this.firstNode = nodeView;
            this.view.div.classed('interpolating1', true);
        } else {
            this.graphController.pathController
                .makePathBetween(this.firstNode, nodeView);
            delete this.firstNode;
            this.view.div.classed('interpolating1', true);
            this.graphController.state.set('NONE');
        }
    }

    /**
     * link the given node
     * @param {NodeView} nodeView
     */
    onClickLinkNode(nodeView) {
        if (!this.hasOwnProperty('firstNode')) {
            this.firstNode = nodeView;
            this.view.div.classed('linking1', true);
        } else {
            this.graphController
                .addLink('', this.firstNode.id, nodeView.id);
            delete this.firstNode;
            this.view.div.classed('linking1', false);
            this.graphController.state.set('NONE');
        }
    }

    /**
     * when hovered, highlights searchable strings
     *  in the metadata: e.g. names, dates, etc.
     */
    onMetaDataMouseOver() {
        let nodeId = this.view.tooltip.attr('data-node');
        let query = d3.select(window.event.target).text().replace(/\"/g, '');
        let isURL = (str) => /http/.test(str) || /base64/.test(str);

        let validQuery = !(isURL(query) || _.isEmpty(query));

        if (validQuery) {
            d3.select(window.event.target)
                .attr('i', nodeId)
                .attr('q', query)
                .classed('active', true);
        } else if (isURL(query)) {
            d3.select(window.event.target)
                .attr('url', query)
                .classed('link', true);
        }
    }

    /**
     * when unhovered, unhighlights searchable strings in the metadata
     */
    onMetaDataMouseOut() {
        d3.select(window.event.target).classed('active', false);
        d3.select(window.event.target).classed('link', false);
        window.event.preventDefault();
        window.event.stopPropagation();
        d3.event.stopPropagation();
        d3.event.preventDefault();
    }

    /**
     * opens the given link in a new window
     * @param {string} url the link to follow
     */
    goToLink(url) {
        window.open(url);
    }

    /**
     * when clicked, searches a string from the metadata
     */
    onMetaDataClick() {
        if (d3.select(window.event.target).classed('active')) {
            this.graphController.expand(
                parseInt(d3.select(window.event.target).attr('i')),
                d3.select(window.event.target).attr('q')
            );
        } else if (d3.select(window.event.target).classed('link')) {
            this.goToLink(d3.select(window.event.target).attr('url'));
        }
    };

    /**
     * called when the user starts dragging
     *  onto an image after having clicked 'crop'
     * @param {NodeView} nodeView
     */
    onClipDragStart(nodeView) {
        nodeView.el = window.event.target;
        let [mouseX, mouseY] = d3.mouse(nodeView.el);
        nodeView.clipStartX = mouseX - nodeView.width / 2;
        nodeView.clipStartY = mouseY - nodeView.height / 2;

        this.view.clipPreview
            .attr('x', nodeView.clipStartX)
            .attr('y', nodeView.clipStartY);
    }

    /**
     * called when the user is still dragging onto an image
     * @param {NodeView} nodeView
     */
    onClipDragMove(nodeView) {
        let [mouseX, mouseY] = d3.mouse(nodeView.el);
        centerX = nodeView.width / 2;
        centerY = nodeView.height / 2;
        nodeView.clipCurrentX = mouseX - centerX - nodeView.clipStartX;
        nodeView.clipCurrentY = mouseY - centerY - nodeView.clipStartY;

        // in case reverse crop
        nodeView.clipTranslateX =
            nodeView.clipCurrentX < 0 ? nodeView.clipCurrentX : 0;
        nodeView.clipTranslateY =
            nodeView.clipCurrentY < 0 ? nodeView.clipCurrentY : 0;
        let clipTranslateString =
            `translate(${nodeView.clipTranslateX} ${nodeView.clipTranslateY})`;

        // updates the clip preview to reflect mouse move
        this.view.clipPreview
            .attr('fill-opacity', .6)
            .attr('width', Math.abs(nodeView.clipCurrentX))
            .attr('height', Math.abs(nodeView.clipCurrentY))
            .attr('transform', clipTranslateString)
            .raise();
    }

    /**
     * called when the user is finished dragging onto an image
     * @param {NodeView} nodeView
     */
    onClipDragEnd(nodeView) {
        this.view.clipPreview.attr('fill-opacity', 0).lower();

        // update clip attributes to reflect selection
        nodeView.crop(
            parseInt(this.view.clipPreview.attr('x')) + nodeView.width / 2,
            parseInt(this.view.clipPreview.attr('y')) + nodeView.height / 2,
            this.view.clipPreview.attr('width'),
            this.view.clipPreview.attr('height'),
            nodeView.clipTranslateX,
            nodeView.clipTranslateY
        );
    }
}
